import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../../context/auth-context";
import { formatDate, formatToZMW, rootUrl } from "../../../helpers";
import { FaArrowDown, FaArrowUp, FaSpinner } from "react-icons/fa";
import { useOutletContext } from "react-router-dom";

export default function LoanSummary({ sid, onAddPayment, onClose }) {
  const { allData } = useOutletContext();
  const auth = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (sid) {
      fetchSummary();
    }
  }, [sid]);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(rootUrl(`/getLoanSummary/${sid}`));
      if (response.data.status === 200) {
        setData(response.data);
      } else {
        setError(response.data.msg || "Failed to load");
      }
    } catch (err) {
      setError(err?.response?.data?.msg || err?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <FaSpinner className="animate-spin text-4xl text-blue-950 mb-3" />
        <p className="text-gray-600">Loading loan summary...</p>
      </div>
    );
  }

  if (error || !data?.supplier) {
    return (
      <div className="text-center py-8 text-red-600">
        <p>{error || "Loan not found"}</p>
      </div>
    );
  }

  const { supplier, transactions, summary } = data;
  const getTxKind = (t) => {
    const txType = String(t?.tx_type || "").toLowerCase().trim();
    if (txType === "repayment") return "repayment";
    if (txType === "disbursement") return "disbursement";
    // Keep disbursement as safe default so principal leg is never mislabeled.
    return "disbursement";
  };
  const isRepayment = (t) => getTxKind(t) === "repayment";

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-950 to-blue-800 rounded-xl p-4 text-white">
        <h3 className="font-semibold text-lg">{supplier.name}</h3>
        {supplier.person && (
          <p className="text-blue-100 text-sm">{supplier.person}</p>
        )}
        {supplier.pnumber && (
          <p className="text-blue-100 text-sm">{supplier.pnumber}</p>
        )}
        {summary.dueDate && (
          <p className={`text-sm mt-2 ${summary.isOverdue ? "text-red-200 font-medium" : "text-blue-100"}`}>
            Due: {summary.dueDate}
            {summary.periodMonths ? ` (${summary.periodMonths} months)` : ""}
            {summary.isOverdue ? " — Overdue (+2% interest applied)" : ""}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
          <p className="text-xs text-gray-500 uppercase">Principal</p>
          <p className="font-semibold text-gray-900">{formatToZMW(summary.principal)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
          <p className="text-xs text-gray-500 uppercase">Base (5%)</p>
          <p className="font-semibold text-gray-900">{formatToZMW(summary.baseInterest ?? summary.interest)}</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
          <p className="text-xs text-amber-700 uppercase">Overdue (2%)</p>
          <p className="font-semibold text-amber-800">{formatToZMW(summary.overdueInterest || 0)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
          <p className="text-xs text-gray-500 uppercase">Total Owed</p>
          <p className="font-semibold text-gray-900">{formatToZMW(summary.totalOwed)}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3 border border-green-100">
          <p className="text-xs text-green-700 uppercase">Repayments</p>
          <p className="font-semibold text-green-800">{formatToZMW(summary.payments)}</p>
          <p className="text-[10px] text-green-600 mt-0.5">From cashbook 157004</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
          <p className="text-xs text-blue-700 uppercase">Balance</p>
          <p className="font-bold text-blue-950 text-lg">{formatToZMW(summary.balance)}</p>
        </div>
      </div>

      {summary.balance > 0 && (
        <button
          type="button"
          onClick={() => onAddPayment({
            sid: supplier.id,
            name: supplier.name,
            person: supplier.person,
            principal: summary.principal,
            interest: summary.interest,
            totalOwed: summary.totalOwed,
            payments: summary.payments,
            balance: summary.balance,
          })}
          className="w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-lg font-medium"
        >
          <FaArrowDown className="text-sm" />
          Add Repayment
        </button>
      )}

      <div>
        <h4 className="font-semibold text-gray-800 mb-3">Transaction History</h4>
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Date</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Ref</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Description</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-600">Amount</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                    No transactions
                  </td>
                </tr>
              ) : (
                transactions.map((t, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="px-4 py-2 text-gray-700">{formatDate(t.date)}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                          isRepayment(t)
                            ? "bg-green-100 text-green-800"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {isRepayment(t) ? (
                          <>
                            <FaArrowDown className="text-[10px]" /> Repayment
                          </>
                        ) : (
                          <>
                            <FaArrowUp className="text-[10px]" /> Disbursement
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-700">{t.invoice || "-"}</td>
                    <td className="px-4 py-2 text-gray-700">{t.comment || "-"}</td>
                    <td className="px-4 py-2 text-right font-medium">
                      {isRepayment(t) ? (
                        <span className="text-green-700">-{formatToZMW(t.amount)}</span>
                      ) : (
                        <span className="text-amber-700">+{formatToZMW(t.amount)}</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
